'use client';

import { motion } from 'framer-motion';
import { X, Heart } from 'lucide-react';
import type { KeyboardLayout } from '@/utils/keyboardLayouts';

interface SettingsModalProps {
  keyboardLayout: KeyboardLayout;
  onToggleKeyboard: () => void;
  onClose: () => void;
  strings: {
    title: string;
    supportTitle: string;
    supportBody: string;
    supportCta: string;
    creditsTitle: string;
    creditsGameBy: string;
    creditsDesignInspiredBy: string;
    closeLabel: string;
  };
}

export function SettingsModal({
  keyboardLayout,
  onToggleKeyboard,
  onClose,
  strings,
}: SettingsModalProps) {
  const isQwerty = keyboardLayout === 'QWERTY';

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      >
        <motion.div
          className="relative w-full max-w-sm rounded-xl bg-neutral-900 p-5 text-neutral-100 shadow-xl"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        onClick={e => e.stopPropagation()}
        >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{strings.title}</h2>
          <button onClick={onClose}>
            <X className="h-5 w-5 text-neutral-400 hover:text-neutral-200" />
          </button>
        </div>
  
        <div className="mb-6">
          <div
            onClick={onToggleKeyboard}
            className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-800 px-3 py-2"
          >
            <span className="text-sm">QWERTY</span>

            <div
              className={`relative h-6 w-11 rounded-full transition ${
                isQwerty ? 'bg-emerald-600' : 'bg-neutral-600'
              }`}
            >
              <span
                className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${
                  isQwerty ? 'left-6' : 'left-1'
                }`}
              />
            </div>
          </div>
        </div>
  
        <div className="mb-6 rounded-lg bg-neutral-800 px-3 py-3">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Heart className="h-4 w-4 text-emerald-500" />
            {strings.supportTitle}
          </h3>

          <p className="mb-3 text-sm text-neutral-400">
            {strings.supportBody}
          </p>

          <a
            href="https://buymeacoffee.com/mariofyl"
            target="_blank"
            rel="noopener noreferrer"
            className="
              block w-full rounded-md
              bg-emerald-600 py-2
              text-center text-sm font-bold
              text-white hover:bg-emerald-500
            "
          >
            {strings.supportCta}
          </a>
        </div>
  
        <h2 className="mb-2 text-lg font-semibold">{strings.creditsTitle}</h2>
        <div className="space-y-2 text-sm text-neutral-400">
          <p>
            {strings.creditsGameBy}{' '}
            <a
              href="https://www.linkedin.com/in/mario-fayolle/"
              target="_blank"
              className="text-neutral-200 hover:underline"
            >
              @mariofayolle
            </a>
          </p>
          <p>
            {strings.creditsDesignInspiredBy}{' '}
            <a
              href="https://wordle.louan.me/"
              target="_blank"
              className="text-neutral-200 hover:underline"
            >
              wordle.louan.me
            </a>
          </p>
        </div>
  
        <button
          onClick={onClose}
          className="
            mt-5 w-full rounded-md
            bg-neutral-700 py-2
            text-sm font-bold
            hover:bg-neutral-600
          "
        >
          {strings.closeLabel}
        </button>
      </motion.div>
    </motion.div>
  );
}
